import React, { useMemo, useState } from 'react';
import { getGameData } from '../services/dataLoader';
import { Card, CardContent, CardHeader } from './ui/card';
import { Button } from './ui/button';

const buildPromptTemplate = (countryList: string) => `You are generating 5 geography questions for a country quiz game.

Rules:
- Provide exactly 5 questions.
- Each question must have 5–40 valid country answers.
- Answers must be ISO3 codes from the provided country list.
- Return JSON with this shape:
{
  "questions": [
    {
      "id": "q1",
      "text": "Question text here",
      "answers": ["USA", "CAN"]
    }
  ]
}

Country List (Name | ISO2 | ISO3):
${countryList}
`;

export const PromptHelperPage: React.FC = () => {
  const data = getGameData();
  const [copied, setCopied] = useState<string | null>(null);

  const countries = useMemo(() => {
    return [...data.countries].sort((a, b) => a.name.localeCompare(b.name));
  }, [data.countries]);

  const countryListText = useMemo(() => {
    return countries.map((country) => `${country.name} | ${country.iso2} | ${country.iso3}`).join('\n');
  }, [countries]);

  const promptTemplate = useMemo(() => buildPromptTemplate(countryListText), [countryListText]);

  const handleCopy = async (value: string, label: string) => {
    await navigator.clipboard.writeText(value);
    setCopied(label);
    window.setTimeout(() => setCopied(null), 1500);
  };

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-8 px-4 py-8">
      <div className="flex flex-col gap-2">
        <h2 className="text-2xl font-bold text-foreground">LLM Prompt Helper</h2>
        <p className="text-sm text-muted-foreground">
          Copy country data and the prompt template to generate new question sets.
        </p>
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
            <div>
              <h3 className="text-lg font-semibold">Prompt Template</h3>
              <p className="text-xs text-muted-foreground">
                Use this prompt with the country list below to generate questions that match the game rules.
              </p>
            </div>
            <Button variant="outline" onClick={() => handleCopy(promptTemplate, 'prompt')}>
              {copied === 'prompt' ? 'Copied!' : 'Copy Prompt'}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          <textarea
            className="min-h-[240px] w-full rounded-md border border-border bg-background p-3 text-xs text-foreground"
            readOnly
            value={promptTemplate}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
            <div>
              <h3 className="text-lg font-semibold">Country List</h3>
              <p className="text-xs text-muted-foreground">
                Full list of countries with ISO2/ISO3 codes, ready to paste into your prompt.
              </p>
            </div>
            <Button variant="outline" onClick={() => handleCopy(countryListText, 'countries')}>
              {copied === 'countries' ? 'Copied!' : 'Copy Country List'}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="max-h-[360px] overflow-auto rounded-md border border-border">
            <table className="min-w-full text-left text-xs">
              <thead className="sticky top-0 bg-card text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 font-semibold">Country</th>
                  <th className="px-3 py-2 font-semibold">ISO2</th>
                  <th className="px-3 py-2 font-semibold">ISO3</th>
                </tr>
              </thead>
              <tbody>
                {countries.map((country) => (
                  <tr key={country.iso3} className="border-t border-border/60">
                    <td className="px-3 py-2 font-medium">{country.name}</td>
                    <td className="px-3 py-2 text-muted-foreground">{country.iso2}</td>
                    <td className="px-3 py-2 text-muted-foreground">{country.iso3}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
